import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { NotFound } from 'common/components/NotFound';
import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { selectPageInfo, selectRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFetchEntities } from 'common/hooks/usePalmFetchEntities';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { fetchLicenses } from 'common/services/api/Licenses/Licenses.thunk';
import { FetchTenantDetailsResponse } from 'common/services/api/Tenants/TenantThunkTypeDefs';
import { EntitiesStatus } from 'common/types/Entities';
import {
    ActionDefinition,
    ColumnDefinition,
    ColumnType,
    Operations } from 'common/types/TypeDefs';

import { LicenseCreateContent } from './LicensesCreateDrawerContent';
import { LicensesDeleteDialogContent } from './LicensesDeleteDialogContent';
import {
    addNewFilter,
    checkStatusFilter,
    clearFilters,
    removeFilter,
    selectFilters,
} from './licensesSlice';

export function LicensesTab() {
    const typeName = 'License';

    const dispatch = useAppDispatch();
    const [processing, setProcessing] = useState<boolean>(false);
    const rows = useAppSelector(selectRows) ?? [];

    const dropdownProps = [
        {
            dropdownInfo: {
                id: 'status',
                label: 'Status',
                searchOptions: ['option1', 'option2'],
                isSearch: false,
                hasClear: false,
                checkBoxes: rows.length ?
                    [
                        {
                            label: EntitiesStatus.ACTIVE
                        },
                        {
                            label: EntitiesStatus.INACTIVE
                        }
                    ] :
                    []
            },
            dropdownReducer: (label: string) => {
                dispatch(checkStatusFilter(label));
            },
        }
    ];
    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters,
        dropdownProps);

    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);
    const shouldDisableOpenButton = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager, Roles.MultiTenantRO, Roles.UserManager ]);
    const [sortModel, setSortModel] = usePalmSortModel('billingRecord');

    const { tenantId = '', suiteId = '' } = useParams();

    const pageInfo = useAppSelector(selectPageInfo) as FetchTenantDetailsResponse;
    const { tenantRequiresLicense: isLicenseRequired } = pageInfo;

    const fetchEntities = usePalmFetchEntities(
        typeName,
        { tenantId },
        fetchLicenses,
        setProcessing
    );

    const onGetRowLink = useCallback((licenseId: string) => {
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/licenses/${licenseId}`;
    }, []);

    const actionDefinitions = [
        { action: Operations.Open, disabled: shouldDisableOpenButton, urlToOpen: onGetRowLink },
        { action: Operations.Edit, disabled: shouldDisableButtons },
        { action: Operations.Delete, disabled: shouldDisableButtons }
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'id', columnName: 'License ID', columnType: ColumnType.First, shouldFilterByExactMatch: true },
        { columnId: 'companyKey', columnName: 'Company Key', columnType: ColumnType.Tooltip },
        { columnId: 'billingSystemName', columnName: 'Billing System', columnType: ColumnType.Tooltip },
        { columnId: 'billingRecord', columnName: 'Billing Record', columnType: ColumnType.Tooltip, copy: true },
        { columnId: 'status', columnName: 'Status', columnType: ColumnType.Chip },
        { columnId: 'startDate', columnName: 'Start Date', columnType: ColumnType.DatetimeTooltip },
        { columnId: 'endDate', columnName: 'End Date', columnType: ColumnType.DatetimeTooltip },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action, size: 0.6 }
    ];

    if (!isLicenseRequired) {
        return <NotFound useSpacing/>;
    }

    return <PalmDataGrid
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        title='License'
        getRowLink={shouldDisableOpenButton ? undefined : onGetRowLink}
        deleteFunction={LicensesDeleteDialogContent}
        isCreateButtonDisabled={shouldDisableButtons}
        filter={filterProp}
        processing={processing}
        contentFunction={LicenseCreateContent}
        refreshDataGridClick={() => fetchEntities()}
        sortModel={sortModel}
        clientSortHandler={setSortModel}
        shouldShowExportButton={true}
    />;
}
